import { RadioChangeEvent, Space } from 'antd';
import styles from './index.module.scss';
import dayjs, { Dayjs } from 'dayjs';
import TimeCarousel from '../TimeCarousel';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Select, DatePicker, Form, Radio, Button, FormTitle } from '@maxtropy/components';
import { DateType, DateTypeDisplay, overallGetTime } from '../utils';
import { getDataQueryPeriodList } from '@/api/energyOverview';
import MakeUseOf, { StateEnergyProps } from '../MakeUseOf';
import KpiNorm from '../KpiNorm';
import RunState from '../RunState';
import ModalCompare from '../ModalCompare';
import { useHasPermission } from '@/utils/utils';
import { PermissionsType } from '@/common/permissionsConst';
import { EnergyMonitorContext } from '../';
import CalcModal from '../CalcModal';
import ExportEnergyModal from '../ExportEnergyModal';
import useLatestRequest from '../useLatestRequest';

export type RangeValue = [Dayjs | null, Dayjs | null] | null;

const OverView: React.FC = () => {
  const { unitId, deviceName } = React.useContext(EnergyMonitorContext);
  const [timeResolution, setTimeResolution] = useState<DateType>(DateType.DAY);
  const [displayValue, setDisplayValue] = useState<Dayjs>(dayjs());
  const timeRangeTidy = useMemo(() => {
    const { startTime, endTime } = overallGetTime(timeResolution, displayValue);
    return [startTime, endTime];
  }, [timeResolution, displayValue]);
  const [energyMediumId, setEnergyMediumId] = useState<string>();
  const [open, setOpen] = useState<boolean>(false);
  const [showCalcModal, setShowCalcModal] = useState<boolean>(false);
  const [showExportModal, setShowExportModal] = useState<boolean>(false);
  const [dataPeriodList, setDataPeriodList] = useState<any[]>([]);
  const [dataQueryPeriodId, setDataQueryPeriodId] = useState<number>();
  const [dataQueryPeriodValue, setDataQueryPeriodValue] = useState<string>();
  const dataComparison = useHasPermission(PermissionsType.B_DATACOMPARISON);
  const [stateChangeData, setStateChangeData] = useState<StateEnergyProps>();
  const [form] = Form.useForm();
  const { runLatest } = useLatestRequest();
  const onRadioChange = (e: RadioChangeEvent) => {
    const value: DateType = e.target.value;
    setTimeResolution(value);
    setDisplayValue(dayjs());
  };

  const disabledDate = (current: Dayjs) => {
    return current && current >= dayjs().endOf('day');
  };

  useEffect(() => {
    setDataQueryPeriodId(undefined);
    form.setFieldValue('dataQueryPeriodId', undefined);
    setDataQueryPeriodValue(undefined);
    setDataPeriodList([]);

    runLatest(
      () =>
        getDataQueryPeriodList({
          startTime: timeRangeTidy[0],
          endTime: timeRangeTidy[1],
        }),
      {
        onSuccess: res => setDataPeriodList(res.dataPeriodList ?? []),
      }
    );
  }, [form, runLatest, timeRangeTidy]);

  const stateOnChange = useCallback((res: StateEnergyProps) => {
    setStateChangeData(res);
  }, []);

  const picker = useMemo(() => {
    if (timeResolution === DateType.MONTH) {
      return 'month';
    } else if (timeResolution === DateType.WEEK) {
      return 'week';
    } else if (timeResolution === DateType.YEAR) {
      return 'year';
    } else {
      return 'date';
    }
  }, [timeResolution]);

  const inceptMediumId = useCallback((str?: string) => {
    setEnergyMediumId(str);
  }, []);

  return (
    <div className={styles.wrapper}>
      <FormTitle title={deviceName} style={{ paddingLeft: 12, margin: '12px 0 16px 0' }} />
      {/* 日期选择器 */}
      <div className={styles.dateForm}>
        <Space size={8} style={{ padding: '0px 12px' }}>
          <Radio.Group onChange={onRadioChange} value={timeResolution} buttonStyle="solid">
            <Radio.Button value={DateType.DAY}>{DateTypeDisplay[DateType.DAY]}</Radio.Button>
            <Radio.Button value={DateType.WEEK}>{DateTypeDisplay[DateType.WEEK]}</Radio.Button>
            <Radio.Button value={DateType.MONTH}>{DateTypeDisplay[DateType.MONTH]}</Radio.Button>
            <Radio.Button value={DateType.YEAR}>{DateTypeDisplay[DateType.YEAR]}</Radio.Button>
          </Radio.Group>
          <DatePicker
            picker={picker}
            style={{ width: '250px' }}
            value={displayValue}
            disabledDate={disabledDate}
            onChange={setDisplayValue}
            allowClear={false}
          />
        </Space>
        <Space>
          <Form form={form} isOrigin={true}>
            <Form.Item name="dataQueryPeriodId" label="数据查询时段" style={{ marginBottom: 0 }}>
              <Select
                placeholder="请选择"
                style={{ width: 150 }}
                onChange={(value: number) => {
                  if (value === undefined) {
                    setDataQueryPeriodValue(''); // 清空时设置为空字符串
                    setDataQueryPeriodId(undefined); // 同时清空id
                    return;
                  }
                  const findRes = dataPeriodList.find(i => i.id === value);
                  const res = findRes.name ?? '';

                  setDataQueryPeriodValue(res);
                  setDataQueryPeriodId(value);
                }}
                options={dataPeriodList.map(item => ({ value: item.id, label: item.name }))}
                allowClear
              />
            </Form.Item>
          </Form>
        </Space>

        <Space size={8} style={{ marginLeft: 'auto', paddingRight: '12px' }}>
          <Button type="primary" onClick={() => setShowCalcModal(true)}>
            重计算
          </Button>
          <Button type="primary" onClick={() => setShowExportModal(true)}>
            导出用能数据
          </Button>
          {dataComparison && (
            <Button type="primary" onClick={() => setOpen(true)}>
              数据对比
            </Button>
          )}
        </Space>
      </div>

      <TimeCarousel
        timeResolution={timeResolution}
        timeRangeTidy={timeRangeTidy}
        dataQueryPeriodId={dataQueryPeriodId}
        dataQueryPeriodValue={dataQueryPeriodValue}
        stateChangeData={stateChangeData}
      />

      <div>
        <MakeUseOf
          timeResolution={timeResolution}
          timeRangeTidy={timeRangeTidy}
          dataQueryPeriodId={dataQueryPeriodId}
          stateOnChange={stateOnChange}
          onSendMediumId={inceptMediumId}
        />
      </div>

      <div>
        <KpiNorm timeResolution={timeResolution} timeRangeTidy={timeRangeTidy} dataQueryPeriodId={dataQueryPeriodId} />
      </div>
      <div>
        <RunState
          timeResolution={timeResolution}
          timeRangeTidy={timeRangeTidy}
          dataQueryPeriodId={dataQueryPeriodId}
          dataQueryPeriodValue={dataQueryPeriodValue}
        />
      </div>
      {open && <ModalCompare setOpen={setOpen} />}
      {showCalcModal && <CalcModal isShow={setShowCalcModal} />}
      {showExportModal && (
        <ExportEnergyModal
          visible={showExportModal}
          onClose={() => setShowExportModal(false)}
          energyMediumId={energyMediumId}
          energyUnitId={unitId}
          dataQueryPeriodId={dataQueryPeriodId}
        />
      )}
    </div>
  );
};
export default OverView;
